/**
 * What Project Sola sends to Gemini, and everything it structurally cannot.
 *
 * **This is not the export serializer and must never become it** (D-093 §2). A
 * manual export produces a file the user inspects before sharing; an API
 * request transmits immediately after confirmation. The export contract
 * deliberately supports optional sensitive material — photos, notes, exact
 * dates — behind privacy flags, and **an AI request must not be *capable* of
 * representing any of it.** Those are different privacy assumptions and one
 * serializer cannot hold both. D-085 continues to govern the export unchanged;
 * this is a second boundary with its own guard.
 *
 * **The forbidden data is absent from the types, not filtered out afterwards.**
 * A redaction filter is a thing that can be forgotten, mis-ordered, or written
 * to cover only the fields that existed when it was written — which is the
 * export-gating failure this project has now recorded four times. A type with
 * nowhere to put a photo cannot leak one.
 *
 * **The vocabularies below are declared here rather than imported from
 * `models/`, deliberately.** This is a wire format with its own version, and
 * coupling it to internal types would let an ordinary internal rename silently
 * change what goes over the network. The duplication is small, and
 * `request.test.ts` asserts the two stay in step.
 */

import type { GeminiFailure } from './errors'

/* ------------------------------------------------------------------------- */
/* Vocabularies. Every one is closed — there is no free-text field anywhere    */
/* in this payload, which is what makes "no names, no notes" structural.       */
/* ------------------------------------------------------------------------- */

export const WIRE_DOMAINS = ['bodyComposition', 'cardio', 'strength', 'mobility'] as const
export type WireDomain = (typeof WIRE_DOMAINS)[number]

/**
 * `inactive` is deliberately absent.
 *
 * An inactive domain is not described to Gemini as inactive — it is **not in
 * the payload at all**. This mirrors D-092's backfill rule, where an absent
 * domain goal *is* an inactive domain and writing an `inactive` row would
 * assert a choice the user never made. Sending one would make the same
 * assertion to a third party.
 */
export const WIRE_INTENTIONS = ['improve', 'maintain', 'trackOnly'] as const
export type WireIntention = (typeof WIRE_INTENTIONS)[number]

export const WIRE_PHASE_TYPES = [
  'fatLoss',
  'maintenance',
  'leanBulk',
  'miniCut',
  'aestheticPrep',
] as const
export type WirePhaseType = (typeof WIRE_PHASE_TYPES)[number]

/** Direction of travel, never a magnitude ordering (the `ratePosition` lesson). */
export const WIRE_TREND_DIRECTIONS = ['rising', 'steady', 'falling', 'unknown'] as const
export type WireTrendDirection = (typeof WIRE_TREND_DIRECTIONS)[number]

export const WIRE_RATE_POSITIONS = ['faster', 'inRange', 'slower', 'unknown'] as const
export type WireRatePosition = (typeof WIRE_RATE_POSITIONS)[number]

/** The published 0.50 boundary and the coaching bands around it (D-071). */
export const WIRE_CONDITION_BANDS = ['below', 'within', 'above', 'unknown'] as const
export type WireConditionBand = (typeof WIRE_CONDITION_BANDS)[number]

export const WIRE_CONFIDENCE = ['low', 'moderate', 'high'] as const
export type WireConfidence = (typeof WIRE_CONFIDENCE)[number]

/**
 * Adherence as the rules engine already grades it.
 *
 * `unknown` is a blank field, never a poor week — the same distinction the
 * engine makes, carried onto the wire so a reviewer cannot read a missing
 * answer as a failure.
 */
export const WIRE_ADHERENCE = ['good', 'mixed', 'tooLowToJudge', 'unknown'] as const
export type WireAdherence = (typeof WIRE_ADHERENCE)[number]

export const WIRE_EVIDENCE_STATES = ['sufficient', 'partial', 'missing'] as const
export type WireEvidenceState = (typeof WIRE_EVIDENCE_STATES)[number]

/* ------------------------------------------------------------------------- */
/* The payload.                                                               */
/* ------------------------------------------------------------------------- */

export interface WireDomainGoal {
  readonly domain: WireDomain
  readonly intention: WireIntention
  readonly primary: boolean
  /** Planned sessions a week, where the user has committed to a number. */
  readonly plannedSessionsPerWeek?: number
  /** Sessions actually recorded this week. Absent is not zero. */
  readonly recordedSessions?: number
}

export interface WirePhaseContext {
  readonly type: WirePhaseType
  /**
   * Week N of the phase, never a calendar date (D-060's `dayIndex` reasoning).
   * Ordering and elapsed time survive; the date does not.
   */
  readonly weekIndex: number
  readonly plannedWeeks?: number
  /** The user's own configured range, as a percentage of body weight a week. */
  readonly targetRateLowPercent: number
  readonly targetRateHighPercent: number
  readonly ratePosition: WireRatePosition
}

export interface WireTrends {
  readonly weightDirection: WireTrendDirection
  /** Change over the window, as a percentage. Never an absolute series. */
  readonly weightChangePercent?: number
  readonly waistDirection: WireTrendDirection
  readonly waistChangePercent?: number
  readonly conditionBand: WireConditionBand
}

export interface WireSummaries {
  readonly adherence: WireAdherence
  /** Recovery and strength arrive as plain numbers from `evidenceSummary.ts`. */
  readonly recoveryScore?: number
  readonly strengthTrend?: WireTrendDirection
  readonly cardioTrend?: WireTrendDirection
  readonly mobilityTrend?: WireTrendDirection
}

export interface WireConstraints {
  readonly sessionsAvailablePerWeek?: number
  readonly minutesPerSession?: number
  readonly hasGymAccess?: boolean
}

export interface WireEvidence {
  readonly weighIns: WireEvidenceState
  readonly measurements: WireEvidenceState
  readonly training: WireEvidenceState
  readonly nutrition: WireEvidenceState
  readonly confidence: WireConfidence
}

/**
 * The opt-in measurements block (D-093 §2).
 *
 * **Off by default and chosen per request**, listed in the preview, and
 * **limited to exactly these four values**. It is never expanded to raw
 * history, to the neck, to notes, to photos or to date of birth — the neck in
 * particular is absent because it buys nothing a reviewer can use and is one
 * more circumference disclosed.
 */
export interface WireMeasurements {
  readonly weightKg: number
  readonly waistCm: number
  readonly heightCm: number
  readonly estimatedBodyFatPercent?: number
}

/**
 * The whole of what may be sent.
 *
 * There is nowhere here for a photo, a note, an exact date, a date of birth, a
 * name, a project label, a raw series, a record id, a device id, a push
 * subscription or a credential. That is the point, and `FORBIDDEN_KEYS` below
 * turns it from a property of this file into something a test can fail on.
 */
export interface GeminiRequestPayload {
  readonly contractVersion: number
  readonly domains: readonly WireDomainGoal[]
  readonly phase?: WirePhaseContext
  readonly trends: WireTrends
  readonly summaries: WireSummaries
  readonly constraints: WireConstraints
  readonly evidence: WireEvidence
  readonly measurements?: WireMeasurements
  /**
   * Which disclosure profile produced this payload.
   *
   * Stored locally with the proposal so a past proposal can say what it was
   * shown (D-093 §2). **There is no permanent broad-consent flag** — the
   * per-request confirmation cannot be turned off once and forgotten.
   */
  readonly disclosureProfile: 'default' | 'withMeasurements'
}

/* ------------------------------------------------------------------------- */
/* The guard.                                                                 */
/* ------------------------------------------------------------------------- */

/**
 * Keys that must never appear anywhere in a serialized request.
 *
 * The type system already makes them unreachable; this is the runtime proof,
 * and it exists because **the type system stops at the boundary where someone
 * spreads an internal object into a payload**. `{...record}` typechecks against
 * a wider structural target more often than anyone expects, and a single
 * careless spread is all it takes.
 *
 * D-094 requires a test proving forbidden keys and binary values cannot be
 * serialized. This is what that test drives.
 */
export const FORBIDDEN_KEYS: readonly string[] = [
  /* Photos and any reference to one. */
  'photo',
  'photos',
  'photoId',
  'blobId',
  'thumbnailBlobId',
  'referencedBlobIds',
  'image',
  'mediaFile',
  /* Free text the user wrote. */
  'note',
  'notes',
  'reviewNotes',
  'name',
  'projectName',
  'label',
  /* Anything that pins a real-world date. */
  'date',
  'startDate',
  'endDate',
  'targetDate',
  'capturedDate',
  'weekStartDate',
  'dateOfBirth',
  'birthDate',
  'requestedAt',
  'changedAt',
  'effectiveFrom',
  /* Raw series rather than a summary. */
  'weightEntries',
  'measurementSessions',
  'dailyChecks',
  'wearableImports',
  'sets',
  'entries',
  /* Identifiers of records and devices. */
  'id',
  'projectId',
  'phaseId',
  'reviewId',
  'goalId',
  'installId',
  'deviceId',
  /* Credentials and anything that could carry one. */
  'token',
  'accessToken',
  'apiKey',
  'credential',
  'credentials',
  'subscription',
  'pushSubscription',
]

export interface ForbiddenContent {
  readonly path: string
  readonly reason: 'forbiddenKey' | 'binaryValue' | 'unexpectedType'
}

/**
 * Walks a value and reports everything that may not be transmitted.
 *
 * Returns findings rather than throwing, so the caller decides — the Worker
 * refuses with `invalidSolaPayload`, and the test enumerates. Binary values are
 * caught by type rather than by key, because a `Blob` assigned to an innocent
 * name is exactly the case a key list misses.
 */
export function findForbiddenContent(value: unknown, path = ''): ForbiddenContent[] {
  if (isTransmittableScalar(value)) {
    return []
  }

  if (typeof value !== 'object' || value === null) {
    /* `undefined` is an absent field, which is ordinary and allowed — a missing
       value is never a zero here either. Functions, symbols and bigints are the
       real catch: they would vanish silently through JSON.stringify or throw
       inside it, and reporting them is more useful than either. */
    return value === undefined ? [] : [{ path: path || '(root)', reason: 'unexpectedType' }]
  }

  if (isBinary(value)) {
    return [{ path: path || '(root)', reason: 'binaryValue' }]
  }

  if (Array.isArray(value)) {
    return value.flatMap((item, index) => findForbiddenContent(item, `${path}[${index}]`))
  }

  return scanObject(value, path)
}

/**
 * Extracted rather than suppressed, per D-056.
 *
 * The dispatch above went one over the complexity limit once every value kind
 * was handled, and this is the same call as C6.4's `nutritionContextOn` for the
 * same reason: this function decides what leaves the device, and a warning in
 * code that makes that decision is refactored.
 */
function scanObject(value: object, path: string): ForbiddenContent[] {
  const findings: ForbiddenContent[] = []

  for (const [key, child] of Object.entries(value)) {
    const childPath = path === '' ? key : `${path}.${key}`
    if (FORBIDDEN_KEYS.includes(key)) {
      findings.push({ path: childPath, reason: 'forbiddenKey' })
    }
    findings.push(...findForbiddenContent(child, childPath))
  }

  return findings
}

function isTransmittableScalar(value: unknown): boolean {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
}

function isBinary(value: object): boolean {
  return (
    value instanceof ArrayBuffer ||
    ArrayBuffer.isView(value) ||
    (typeof Blob !== 'undefined' && value instanceof Blob) ||
    (typeof File !== 'undefined' && value instanceof File)
  )
}

/**
 * The result of preparing a request.
 *
 * A `Result` rather than an exception, following the project-wide rule: a
 * payload that failed its own guard must never be reported as ready to send.
 */
export type PreparedRequest =
  | { readonly ok: true; readonly body: string }
  | { readonly ok: false; readonly failure: GeminiFailure; readonly findings: ForbiddenContent[] }

/**
 * Serializes a payload, refusing if anything forbidden reached it.
 *
 * **This is the only function that turns a payload into bytes**, so the guard
 * cannot be bypassed by a caller that builds its own JSON — the same reasoning
 * that makes `serializeWeeklyReport` the single gate for the export.
 */
export function prepareGeminiRequest(payload: GeminiRequestPayload): PreparedRequest {
  const findings = findForbiddenContent(payload)
  if (findings.length > 0) {
    return { ok: false, failure: 'invalidSolaPayload', findings }
  }
  return { ok: true, body: JSON.stringify(payload) }
}
